import styled from "@emotion/styled";
import { useCallback } from "react";
import type { ReactNode, ComponentPropsWithoutRef, MouseEvent, SyntheticEvent } from "react";
import { motion } from "framer-motion";

import { blackA, bp } from "../theme";

/**
 * The dialog element *is* the scrim — it fills the viewport, dims what's behind
 * it, and centres its child. The real `::backdrop` is cleared so the two don't
 * stack up.
 */
const DialogScrim = styled(motion.dialog)`
    position: fixed;
    top: 0;
    left: 0;
    z-index: 1000;
    width: 100vw;
    height: 100vh;
    max-width: 100vw;
    max-height: 100vh;
    margin: 0;
    padding: 16px;
    border: none;
    outline: none;
    background: ${blackA(0.4)};
    backdrop-filter: blur(4px);
    display: flex;
    justify-content: center;
    align-items: flex-end;

    &::backdrop {
        background: transparent;
    }

    @media ${bp.modal} {
        align-items: center;
    }
`;

interface ModalDialogProps extends ComponentPropsWithoutRef<typeof DialogScrim> {
    /** Fires on Escape, on a backdrop click, and on the close control. */
    onClose: () => void;
    /** Required — a modal with no accessible name is unannounceable. */
    "aria-label": string;
    children: ReactNode;
}

/**
 * Modal dialog.
 *
 * Built on the native `<dialog>` + `showModal()` rather than a hand-rolled
 * overlay, because that one call is what buys the accessibility: focus moves
 * into the dialog and is trapped there, the rest of the page goes inert to
 * screen readers, Escape fires `cancel`, and focus returns to the trigger on
 * close. Reimplementing that on a plain div means reimplementing all four, which
 * is exactly how the lobby's QR modal ended up with none of them.
 *
 * Renders a motion.dialog, so it composes with AnimatePresence for enter/exit —
 * the element stays open until the exit animation finishes unmounting it.
 */
export function ModalDialog({ onClose, children, ...props }: ModalDialogProps) {
    // Promote to the top layer as soon as the node exists. A ref callback (not
    // an effect) so it happens before paint and the dialog never renders a frame
    // as a non-modal, untrapped element.
    const openAsModal = useCallback((node: HTMLDialogElement | null) => {
        if (node && !node.open) node.showModal();
    }, []);

    return (
        <DialogScrim
            ref={openAsModal}
            // The native Escape path. preventDefault stops the browser closing
            // the dialog out from under React, so unmounting stays our call and
            // the exit animation still runs.
            onCancel={(e: SyntheticEvent) => { e.preventDefault(); onClose(); }}
            // The scrim fills the viewport, so "clicked the scrim itself" means
            // clicked outside the card.
            onClick={(e: MouseEvent) => { if (e.target === e.currentTarget) onClose(); }}
            {...props}
        >
            {children}
        </DialogScrim>
    );
}
